<script lang="ts" setup>
  import type {
    Partner,
    Product,
    User,
  } from '@solfacil/girassol/dist/types/components/headers/types';
  import { useKeycloak } from '@solfacil/plasma-utils';
  import { storeToRefs } from 'pinia';

  import type {
    Partner as PartialPerson,
    User as Person,
  } from '~/types/ecommerce/generated.codegen';

  import { useDevice } from '@/composables/useDevice';

  const userStore = useUserStore();
  const { user, inPersonification, isAdmin, scoreSolfacilPlus } =
    storeToRefs(userStore);
  const {
    defaultMenusDesktop,
    wholesalerMenusDesktop,
    modulesDesktop,
    menusMobile,
    wholesalerMenusMobile,
    modulesMobile,
  } = useMenus(resolveComponent('nuxt-link'));
  const isOpenSidebar = ref(false);
  const regionModal = ref<boolean>(false);
  const selectPartnerToPersonifyModal = ref(false);
  const { isMobile } = useDevice();
  const perfisForPersonify = computed(() => {
    const perfis = ['admin'];
    if (inPersonification.value) {
      perfis.push('');
    }
    return perfis;
  });

  provide('regionSelectModal', regionModal);
  provide('selectPartnerToPersonifyModal', selectPartnerToPersonifyModal);

  const userInfo = computed(() => {
    return {
      profile: inPersonification.value ? '' : (user.value as Person)?.profile,
      completeName: userStore.userName,
    } as User;
  });

  const partnerInfo = computed(() => {
    return {
      partnerId: userStore.partnerId,
      responsible: {
        cellPhone: inPersonification.value
          ? ''
          : (user.value as Person)?.partner?.recipient?.phone,
        noAccountManager: true,
      },
      solfacilPlus: {
        level: scoreSolfacilPlus.value?.level,
        currentScore: scoreSolfacilPlus.value?.current_score,
        goScore: scoreSolfacilPlus.value?.go_score,
        nextTransitionDate: scoreSolfacilPlus.value?.next_transition_date,
        hasRisk: scoreSolfacilPlus.value?.has_risk,
      },
    } as Partner;
  });

  const productInfo = computed(() => {
    return {
      id: 'shop',
      title: 'Loja',
    } as Product;
  });

  const selectUserAction = (event: string) => {
    if (event === 'inPersonification') {
      selectPartnerToPersonifyModal.value = true;
    }
    if (event === 'umPersonification') {
      finishPersonification();
    }
  };

  const finishPersonification = () => {
    userStore.removePersonifiedUser();
    clearUrlParams();
    location.href = '/';
  };

  const executeLogout = () => {
    useKeycloak().logout();
  };
</script>

<template>
  <main id="app-ecommerce-footer-links" class="app-ecommerce-footer-links">
    <SolSidebar
      :items="userStore.isWholesaler ? wholesalerMenusMobile : menusMobile"
      :product="productInfo"
      :links="modulesMobile"
      white
      block
      class="sidebar"
      :is-open-mobile-sidebar="isOpenSidebar"
      @click-menu="isOpenSidebar = !isOpenSidebar" />
    <SolHeader
      :user="userInfo"
      :partner="partnerInfo"
      :product="productInfo"
      :perfil-personification="perfisForPersonify"
      :in-personification="!inPersonification && isAdmin"
      :um-personification="inPersonification"
      mobile-white
      center
      @click-user="selectUserAction"
      @click-menu="isOpenSidebar = !isOpenSidebar"
      @click-logout="executeLogout">
      <template #icon>
        <SolLink id="brand" @click="navigateTo('/')">
          <img
            src="/icons/brand-loja.svg"
            alt="brand"
            class="brand"
            width="134"
            height="49" />
        </SolLink>
      </template>

      <template #user-info>
        <UserInfoMenu :user="userInfo" />
      </template>

      <template #user-info-mobile="{ openMenu }">
        <div class="icon-user-info">
          <icon-girassol:account-circle class="icon" @click="openMenu()" />
        </div>
      </template>

      <template #header-mobile="{ closeMenu }">
        <UserInfoMenuMobile :user="userInfo" @close="closeMenu()" />
      </template>

      <template #actions>
        <StateSelectionMenu
          v-if="!isMobile"
          :user="user"
          class="state_selection" />
        <CartMenu />
      </template>
    </SolHeader>
    <MenuApplication
      :menus="
        userStore.isWholesaler ? wholesalerMenusDesktop : defaultMenusDesktop
      "
      :modules="modulesDesktop" />
    <StateSelectionMenu :user="user" class="state_selection_mobile" />
    <SharedImpersonateAlert :user="user as PartialPerson" />
    <section class="page">
      <NuxtPage />
    </section>
    <Footer show-links />

    <SharedImpersonateModal />
  </main>
</template>

<style lang="scss">
  .app-ecommerce-footer-links {
    @apply flex flex-col min-h-screen;

    .sidebar {
      @apply hidden;
    }

    > .page {
      @apply flex-1 flex flex-col;
    }

    .brand {
      @apply relative top-1 md:site:top-2;
      @apply h-10 md:site:h-auto;
    }

    .icon-user-info {
      @apply w-md h-md;
      @apply flex items-center justify-center;

      .icon {
        @apply w-2xs h-2xs;
        @apply cursor-pointer;
      }
    }

    .state_selection {
      @apply hidden md:site:flex;
    }

    .state_selection_mobile {
      @apply flex md:site:hidden;
    }
  }
</style>
